/**
 * AYU Coach — Chat-Interface für tiefere Gesundheitsberatung
 * Design: Sacred Dark / Gold-Akzent (#6b7f4e)
 * Features: KI-Chat, DB-Verlauf, Spracheingabe, Session-Sidebar,
 *           Kommunikationsstil-Wahl, Coaching-Stil-Vorschlag,
 *           Bild-Upload, Quick-Replies, TTS, Krisen-Erkennung
 *
 * Basiert auf MENTO AppMento.tsx — angepasst an AYU-Design und Ayurveda-Kontext
 */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useStreamingChat } from "@/hooks/useStreamingChat";
import { useClientData } from "@/hooks/useClientData";
import { trpc } from "@/lib/trpc";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { FeedbackWidget } from "@/components/app/FeedbackWidget";
import { ThumbsFeedback } from "@/components/app/ThumbsFeedback";
import { useSpeechSynthesis } from "@/hooks/useSpeechSynthesis";
import {
  Send,
  MessageCircle,
  Clock,
  Sparkles,
  Heart,
  Leaf,
  Shield,
  AlertCircle,
  RotateCcw,
  Mic,
  MicOff,
  Loader2,
  X,
  Lightbulb,
  Check,
  Volume2,
  VolumeX,
  ImagePlus,
  Phone,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import CrisisAlert, {
  containsCrisisKeywords,
} from "@/components/app/CrisisAlert";
import SessionSidebar from "@/components/app/SessionSidebar";
import DoshaModeIndicator from "@/components/app/DoshaModeIndicator";
import ChatMarkdown from "@/components/app/ChatMarkdown";
import QuickReplies from "@/components/app/QuickReplies";
import SaveRecipeButton from "@/components/app/SaveRecipeButton";
import VoiceChatOverlay from "@/components/app/VoiceChatOverlay";
import { getPersona } from "@shared/persona";
import { useI18n } from "@/contexts/I18nContext";

import { createLogger } from "@/lib/logger";
const log = createLogger("Appayucoach");

interface Message {
  id: string;
  role: "user" | "ayu";
  text: string;
  time: string;
  imageUrl?: string;
}

interface CoachingStyleSuggestion {
  style: string;
  description: string;
}

interface MemoryContextData {
  summaries: {
    id: number;
    chatType: string;
    title: string | null;
    summary: string;
    mood: string | null;
    createdAt: string | Date;
  }[];
  recentSessions: {
    ayu: {
      sessionId: number;
      title: string | null;
      createdAt: string | Date;
      messages: { role: string; content: string; createdAtMs: number }[];
    }[];
    tagebuch: {
      sessionId: number;
      title: string | null;
      mood?: string | null;
      createdAt: string | Date;
      messages: { role: string; content: string; createdAtMs: number }[];
    }[];
  };
  recentDiaryEntries?: {
    content: string;
    createdAtMs: number;
    mood: string | null;
    sessionTitle: string | null;
  }[];
}

/* ─── SessionFeedbackBox — Nutzt jetzt die universelle ThumbsFeedback-Komponente ─── */
function SessionFeedbackBox({
  clientId,
  sessionId,
}: {
  clientId: string;
  sessionId: number | null;
}) {
  return (
    <ThumbsFeedback
      clientId={clientId}
      sessionType="chat"
      sessionRef={sessionId?.toString()}
      context="/app/coach"
      variant="card"
      label="Wie war diese Session?"
    />
  );
}

function buildMemoryBlock(memoryData: MemoryContextData | undefined, personaName: string): string {
  if (!memoryData) return "";
  let block = "";

  if (memoryData.summaries.length > 0) {
    block += "\n\n═══ GEDÄCHTNIS: FRÜHERE GESPRÄCHE ═══";
    block +=
      "\nDu erinnerst dich an alle bisherigen Gespräche. Nutze dieses Wissen natürlich, ohne es aufzuzählen.";
    block += "\nWenn ein Thema wieder auftaucht, knüpfe nahtlos daran an.\n";
    for (const s of memoryData.summaries) {
      const date = new Date(s.createdAt).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
      });
      const type = s.chatType === "tagebuch" ? "Tagebuch" : "Coaching";
      const moodTag = s.mood ? ` [Stimmung: ${s.mood}]` : "";
      block += `\n[${date} — ${type}${moodTag}]\n${s.summary}\n`;
    }
  }

  const recentAll = [
    ...memoryData.recentSessions.ayu.map(s => ({
      ...s,
      type: "Coaching" as const,
    })),
    ...memoryData.recentSessions.tagebuch.map(s => ({
      ...s,
      type: "Tagebuch" as const,
    })),
  ].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  if (recentAll.length > 0) {
    block += "\n\n═══ LETZTE GESPRÄCHE (DETAILS) ═══";
    for (const session of recentAll) {
      const date = new Date(session.createdAt).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
      });
      block += `\n--- ${session.type} vom ${date} ---`;
      for (const m of session.messages.slice(-15)) {
        const speaker = m.role === "user" ? "Nutzer" : personaName;
        const text =
          m.content.length > 400 ? m.content.slice(0, 400) + "…" : m.content;
        block += `\n${speaker}: ${text}`;
      }
    }
  }

  if (
    memoryData.recentDiaryEntries &&
    memoryData.recentDiaryEntries.length > 0
  ) {
    block += "\n\n═══ TAGEBUCH DER LETZTEN 7 TAGE ═══";
    block +=
      "\nDiese Einträge zeigen, was den Nutzer in den letzten Tagen beschäftigt hat. Nutze sie als Kontext.\n";
    for (const entry of memoryData.recentDiaryEntries) {
      const date = new Date(entry.createdAtMs).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
      const moodTag = entry.mood ? ` [Stimmung: ${entry.mood}]` : "";
      block += `\n[${date}${moodTag}]\n${entry.content}\n`;
    }
  }

  return block;
}

function buildApiMessages(
  systemPrompt: string,
  messages: Message[],
  memoryContext?: MemoryContextData,
  documentContext?: string,
  personaName?: string
) {
  const memoryBlock = buildMemoryBlock(memoryContext, personaName || "Annam");
  const docBlock = documentContext
    ? `\n\n═══ HOCHGELADENE DOKUMENTE ═══\nNutze diese Informationen als Hintergrundwissen, wenn relevant.\n\n${documentContext}`
    : "";
  const fullSystemPrompt = systemPrompt + memoryBlock + docBlock;

  const apiMessages: {
    role: "system" | "user" | "assistant";
    content: string;
    imageUrl?: string;
  }[] = [{ role: "system", content: fullSystemPrompt }];
  for (const msg of messages) {
    if (msg.id === "welcome") {
      apiMessages.push({ role: "assistant", content: msg.text });
    } else if (msg.role === "user") {
      apiMessages.push({
        role: "user",
        content: msg.text,
        imageUrl: msg.imageUrl,
      });
    } else {
      apiMessages.push({ role: "assistant", content: msg.text });
    }
  }
  return apiMessages;
}

function formatTime(ms?: number) {
  if (!ms)
    return new Date().toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  return new Date(ms).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mergeTranscriptSegments(
  currentText: string,
  nextText: string
): string {
  const left = currentText.trim();
  const right = nextText.trim();

  if (!left) return right;
  if (!right) return left;

  const needsSpace = !/[\s([{-]$/.test(left) && !/^[,.;:!?%)\]}]/.test(right);
  return `${left}${needsSpace ? " " : ""}${right}`;
}

function parseCoachingStyle(text: string): {
  cleanText: string;
  suggestion: CoachingStyleSuggestion | null;
} {
  const styleRegex = /\[COACHING_STYLE_SUGGESTION\]\s*(.*?)(?:\n|$)/i;
  const match = text.match(styleRegex);
  if (!match) return { cleanText: text, suggestion: null };
  const styleLine = match[1].trim();
  const cleanText = text.replace(match[0], "").trim();
  const colonIdx = styleLine.indexOf(":");
  if (colonIdx > 0) {
    return {
      cleanText,
      suggestion: {
        style: styleLine.substring(0, colonIdx).trim(),
        description: styleLine.substring(colonIdx + 1).trim(),
      },
    };
  }
  return {
    cleanText,
    suggestion: {
      style: styleLine,
      description: "Basierend auf deiner Anamnese empfohlen",
    },
  };
}

function dedupeMessages(msgs: Message[]): Message[] {
  const seen = new Set<string>();
  const out: Message[] = [];
  for (const m of msgs) {
    if (!seen.has(m.id)) {
      seen.add(m.id);
      out.push(m);
    }
  }
  return out;
}

const SESSION_TOPICS = [
  { icon: Heart, label: "Ernährung & Phänotyp", color: "text-rose-700" },
  { icon: Leaf, label: "Tagesroutine", color: "text-emerald-700" },
  { icon: Shield, label: "Stressmanagement", color: "text-cyan-600" },
];

const COMMUNICATION_STYLES = [
  {
    label: "Einfühlsam & sanft",
    emoji: "\u{1F33F}",
    description: "Ruhig, achtsam, behutsam",
  },
  {
    label: "Direkt & ehrlich",
    emoji: "\u{1F3AF}",
    description: "Klar, auf den Punkt, ohne Umwege",
  },
  {
    label: "Freundschaftlich & locker",
    emoji: "☕",
    description: "Wie mit einem guten Freund",
  },
];

export default function AppAyuCoach() {
  const clientData = useClientData();
  const trpcUtils = trpc.useUtils();
  const { t } = useI18n();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [coachingSuggestion, setCoachingSuggestion] =
    useState<CoachingStyleSuggestion | null>(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [showCrisisAlert, setShowCrisisAlert] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileArchiveOpen, setMobileArchiveOpen] = useState(false);
  const [viewingSessionId, setViewingSessionId] = useState<number | null>(null);
  const [viewingMessages, setViewingMessages] = useState<Message[] | null>(
    null
  );
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [showVoiceChat, setShowVoiceChat] = useState(false);
  const [continueVoiceSessionId, setContinueVoiceSessionId] = useState<
    number | null
  >(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const lastChatBubbleRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const processedVoiceTranscriptIdsRef = useRef<Set<string>>(new Set());
  const lastVoiceTranscriptRoleRef = useRef<"user" | "assistant" | null>(null);
  const isSendingRef = useRef(false);

  const uploadImageMutation = trpc.chat.uploadImage.useMutation();

  const handleVoiceTranscriptCommit = useCallback(
    (entry: { id: string; role: "user" | "assistant"; text: string }) => {
      const trimmedText = entry.text.trim();
      if (!trimmedText) return;

      const mappedId = `voice-${entry.id}`;
      if (processedVoiceTranscriptIdsRef.current.has(mappedId)) return;
      processedVoiceTranscriptIdsRef.current.add(mappedId);

      const mappedRole: Message["role"] =
        entry.role === "assistant" ? "ayu" : "user";
      const time = formatTime();

      setMessages(prev => {
        if (prev.some(msg => msg.id === mappedId)) return prev;

        const lastMessage = prev[prev.length - 1];
        const shouldMergeAssistant =
          mappedRole === "ayu" &&
          entry.role === "assistant" &&
          lastVoiceTranscriptRoleRef.current === "assistant" &&
          !!lastMessage &&
          lastMessage.role === "ayu" &&
          lastMessage.id.startsWith("voice-");

        if (shouldMergeAssistant) {
          const next = prev.slice();
          next[next.length - 1] = {
            ...lastMessage,
            text: mergeTranscriptSegments(lastMessage.text, trimmedText),
          };
          return next;
        }

        return [
          ...prev,
          {
            id: mappedId,
            role: mappedRole,
            text: trimmedText,
            time,
          },
        ];
      });

      lastVoiceTranscriptRoleRef.current = entry.role;
      setSessionStarted(true);
      setHistoryLoaded(true);
      setViewingSessionId(null);
      setViewingMessages(null);
    },
    []
  );

  useEffect(() => {
    if (showVoiceChat) {
      processedVoiceTranscriptIdsRef.current.clear();
      lastVoiceTranscriptRoleRef.current = null;
      return;
    }

    lastVoiceTranscriptRoleRef.current = null;
  }, [showVoiceChat, continueVoiceSessionId]);

  const streamingAyuMsgRef = useRef<string>("");
  const streamingChat = useStreamingChat({
    onToken: (_token, fullText) => {
      streamingAyuMsgRef.current = fullText;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.id === "streaming-ayu") {
          // Only update if text actually changed (prevents unnecessary re-renders)
          if (last.text === fullText) return prev;
          // Reuse the same array prefix — only replace the last element
          const next = prev.slice();
          next[next.length - 1] = { ...last, text: fullText };
          return next;
        }
        return prev;
      });
    },
  });
  const saveCoachingStyleMutation =
    trpc.clientAuth.saveCoachingStyle.useMutation();
  const saveVoicePreferenceMutation =
    trpc.clientAuth.saveVoicePreference.useMutation({
      onSuccess: () => {
        trpcUtils.clientAuth.me.invalidate();
      },
    });
  const voice = useVoiceInput(clientData.clientId || "anon");
  const tts = useSpeechSynthesis({
    voicePreference: clientData.voicePreference as
      | "annam"
      | "christopher"
      | undefined,
  });
  const [pendingImage, setPendingImage] = useState<{
    base64: string;
    mimeType: string;
    preview: string;
  } | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const persona = getPersona(clientData.voicePreference);
  const WELCOME_TEXT = `Hallo ${clientData.firstName || "du"}. Ich bin ${persona.name}, dein Ayu Coach.`;
  // Coaching-Stil-Auswahl nur beim ersten Besuch zeigen (wenn noch kein Stil gespeichert)
  const [styleChosen, setStyleChosen] = useState(!!clientData.coachingStyle);

  // Gedächtnis-Kontext laden
  const memoryInput = useMemo(
    () => ({
      clientId: clientData.clientId || "anon",
    }),
    [clientData.clientId]
  );

  const { data: memoryContext } = trpc.chat.getMemoryContext.useQuery(
    memoryInput,
    {
      enabled: !!clientData.clientId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Dokument-Zusammenfassungen laden
  const docSummariesInput = useMemo(
    () => ({
      clientId: clientData.clientId || "anon",
    }),
    [clientData.clientId]
  );

  const { data: documentSummaries } = trpc.documents.summaries.useQuery(
    docSummariesInput,
    {
      enabled: !!clientData.clientId,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000,
    }
  );

  // Stabiler Query-Input
  const historyInput = useMemo(
    () => ({
      clientId: clientData.clientId || "anon",
      chatType: "ayu" as const,
    }),
    [clientData.clientId]
  );

  const { data: historyData, isFetched: historyFetched } =
    trpc.chat.getHistory.useQuery(historyInput, {
      enabled: !!clientData.clientId,
      refetchOnWindowFocus: false,
      staleTime: 0, // Immer frisch laden wenn die Komponente mountet
    });

  const sessionsListInput = useMemo(
    () => ({
      clientId: clientData.clientId || "anon",
      chatType: "ayu_and_voice" as const,
    }),
    [clientData.clientId]
  );

  const { data: archivedSessions } = trpc.chat.getSessions.useQuery(
    sessionsListInput,
    {
      enabled: !!clientData.clientId,
      refetchOnWindowFocus: false,
    }
  );

  // Verlauf aus DB laden
  useEffect(() => {
    if (!historyFetched || !historyData || historyLoaded) return;
    // Speichere die aktuelle Session-ID für die Sidebar
    if (historyData.sessionId) {
      setCurrentSessionId(historyData.sessionId);
    }
    if (historyData.messages.length > 0) {
      const loadedMessages: Message[] = dedupeMessages(
        historyData.messages
          .filter(m => m.role !== "system")
          .map(m => ({
            id: `db-${m.id}`,
            role: m.role === "user" ? ("user" as const) : ("ayu" as const),
            text: m.content,
            time: formatTime(m.createdAtMs),
            imageUrl: m.imageUrl,
          }))
      );
      setMessages(loadedMessages);
      setSessionStarted(true);
    }
    setHistoryLoaded(true);
    // Nach dem Laden von getHistory die Sessions-Liste invalidieren,
    // damit frisch geschlossene (stale) Sessions im Archiv erscheinen
    trpcUtils.chat.getSessions.invalidate();
  }, [historyFetched, historyData, historyLoaded, trpcUtils]);

  // Throttled auto-scroll: only scroll when message count changes (new message added)
  // or when streaming finishes — NOT on every streaming text update
  const prevMsgCountRef = useRef(0);
  const scrollToLatestMessage = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const isMobileViewport =
        typeof window !== "undefined" && window.innerWidth < 1024;
      const target = isMobileViewport
        ? (lastChatBubbleRef.current ?? chatEndRef.current)
        : chatEndRef.current;

      target?.scrollIntoView({ behavior, block: "end" });
    },
    []
  );

  useEffect(() => {
    const currentMessages =
      viewingSessionId && viewingMessages ? viewingMessages : messages;
    const currentCount = currentMessages.length;
    const hasStreamingMsg = currentMessages.some(m => m.id === "streaming-ayu");

    if (currentCount !== prevMsgCountRef.current || isTyping) {
      scrollToLatestMessage("smooth");
      prevMsgCountRef.current = currentCount;
    } else if (hasStreamingMsg) {
      const container = chatScrollRef.current;
      if (container) {
        const isNearBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          150;
        if (isNearBottom) {
          scrollToLatestMessage("smooth");
        }
      }
    }
  }, [
    isTyping,
    messages,
    scrollToLatestMessage,
    viewingMessages,
    viewingSessionId,
  ]);

  useEffect(() => {
    prevMsgCountRef.current = 0;
    requestAnimationFrame(() => {
      scrollToLatestMessage("auto");
    });
  }, [scrollToLatestMessage, viewingSessionId, viewingMessages]);

  useEffect(() => {
    if (!mobileArchiveOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileArchiveOpen]);

  // Handle URL params from Sessions page (Live-Gespräche / Chat-Übungen)
  const [exerciseContextHandled, setExerciseContextHandled] = useState(false);
  const [activeSessionType, setActiveSessionType] = useState<
    string | undefined
  >(undefined);
  const [activeSessionTitle, setActiveSessionTitle] = useState<
    string | undefined
  >(undefined);
  useEffect(() => {
    if (exerciseContextHandled) return;
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const context = params.get("context");
    const sessionId = params.get("sessionId");
    if (!mode || !context) return;
    setExerciseContextHandled(true);
    // Clean URL params without reload
    window.history.replaceState({}, "", window.location.pathname);
    if (mode === "voice") {
      // Store session type and title for VoiceChatOverlay and open it
      if (sessionId) setActiveSessionType(sessionId);
      const title = params.get("title");
      if (title) setActiveSessionTitle(title);
      setContinueVoiceSessionId(null); // New session, not a continuation
      setShowVoiceChat(true);
    } else if (mode === "chat" && clientData.clientId) {
      // For chat exercises, store sessionType for subsequent messages too
      if (sessionId) setActiveSessionType(sessionId);
      setSessionStarted(true);
      setIsTyping(true);
      const streamingMsg: Message = {
        id: "streaming-ayu",
        role: "ayu",
        text: "",
        time: formatTime(),
      };
      setMessages(prev => [...prev, streamingMsg]);
      streamingAyuMsgRef.current = "";

      // Determine the user prompt based on context
      let userPromptText = "Bitte starte die Übung und begrüße mich.";
      if (context.startsWith("mikrobiom:")) {
        const section = context.replace("mikrobiom:", "");
        if (section === "full") {
          userPromptText = "Ich möchte meine gesamte Mikrobiom-Analyse mit dir besprechen. Bitte gib mir eine verständliche Zusammenfassung und erkläre mir die wichtigsten Punkte. Was bedeuten die Ergebnisse für meine Gesundheit?";
        } else if (section === "keyFindings") {
          userPromptText = "Ich möchte die wichtigsten Befunde meiner Mikrobiom-Analyse mit dir besprechen. Bitte erkläre mir die Befunde verständlich und was sie für meine Gesundheit bedeuten.";
        } else if (section === "recommendations") {
          userPromptText = "Ich möchte die Empfehlungen aus meiner Mikrobiom-Analyse mit dir besprechen. Bitte erkläre mir, welche Empfehlungen am wichtigsten sind und wie ich sie umsetzen kann.";
        } else if (section.startsWith("detail:")) {
          const parts = section.split(":");
          const sectionTitle = parts.slice(2).join(":");
          userPromptText = `Ich möchte den Abschnitt "${sectionTitle}" meiner Mikrobiom-Analyse mit dir besprechen. Bitte erkläre mir diesen Teil verständlich und was er für mich bedeutet.`;
        }
      }

      // Use the standard system prompt — the server will inject session-specific addendum via sessionType
      const apiMessages = buildApiMessages(
        clientData.ayuCoachPrompt || "",
        [
          {
            id: "system-start",
            role: "user",
            text: userPromptText,
            time: formatTime(),
          },
        ],
        memoryContext as MemoryContextData | undefined,
        documentSummaries || undefined,
        persona.name
      );
      streamingChat
        .sendStreaming({
          messages: apiMessages,
          clientId: clientData.clientId,
          chatType: "ayu",
          sessionType: sessionId || undefined,
        })
        .then(result => {
          const { cleanText } = parseCoachingStyle(result.content);
          // For mikrobiom context, also show the user's question
          const newMessages: Message[] = [];
          if (context.startsWith("mikrobiom:")) {
            newMessages.push({
              id: `user-mb-${Date.now()}`,
              role: "user" as const,
              text: userPromptText,
              time: formatTime(),
            });
          }
          setMessages(prev => {
            const withoutStreaming = prev.filter(m => m.id !== "streaming-ayu");
            return [
              ...withoutStreaming,
              ...newMessages,
              {
                id: `ayu-${Date.now()}`,
                role: "ayu" as const,
                text: cleanText,
                time: formatTime(),
              },
            ];
          });
          setIsTyping(false);
        })
        .catch(() => {
          setIsTyping(false);
          setError(t("coach.exerciseError"));
        });
    }
  }, [
    exerciseContextHandled,
    clientData.clientId,
    clientData.ayuCoachPrompt,
    memoryContext,
    documentSummaries,
  ]);

  const handleSend = useCallback(async () => {
    // isSendingRef prevents concurrent calls (e.g. rapid clicks or Enter during image upload).
    // isTyping alone is insufficient because it is only set AFTER the upload await completes.
    if (isSendingRef.current || (!input.trim() && !pendingImage) || isTyping || !clientData.clientId)
      return;

    isSendingRef.current = true;
    setError(null);

    // Capture and clear UI state synchronously BEFORE any await so a second call
    // sees empty input/pendingImage and the guard blocks it immediately.
    const capturedInput = input.trim();
    const capturedImage = pendingImage;
    setInput("");
    setPendingImage(null);
    setIsTyping(true);
    if (!sessionStarted) setSessionStarted(true);

    if (containsCrisisKeywords(capturedInput)) {
      setShowCrisisAlert(true);
    }

    // Bild hochladen falls vorhanden
    let uploadedImageUrl: string | undefined;
    if (capturedImage) {
      setImageUploading(true);
      try {
        const uploadResult = await uploadImageMutation.mutateAsync({
          imageBase64: capturedImage.base64,
          mimeType: capturedImage.mimeType,
          clientId: clientData.clientId,
        });
        if (uploadResult.success && uploadResult.url) {
          uploadedImageUrl = uploadResult.url;
        }
      } catch (err) {
        log.error("Image upload error:");
      } finally {
        setImageUploading(false);
      }
    }

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      text: capturedInput || (uploadedImageUrl ? "[Bild gesendet]" : ""),
      time: formatTime(),
      imageUrl: uploadedImageUrl,
    };

    // updatedMessages is for the API call only; use functional updates for UI state
    // so we never depend on stale closure values for the message list.
    const updatedMessages = [...messages, userMsg];
    setMessages(prev => {
      const withoutStreaming = prev.filter(m => m.id !== "streaming-ayu");
      return [...withoutStreaming, userMsg];
    });

    // Add exactly one streaming placeholder, removing any stale one first.
    const streamingMsg: Message = {
      id: "streaming-ayu",
      role: "ayu",
      text: "",
      time: formatTime(),
    };
    setMessages(prev => {
      const withoutStreaming = prev.filter(m => m.id !== "streaming-ayu");
      return [...withoutStreaming, streamingMsg];
    });
    streamingAyuMsgRef.current = "";

    try {
      const apiMessages = buildApiMessages(
        clientData.ayuCoachPrompt,
        updatedMessages,
        memoryContext as MemoryContextData | undefined,
        documentSummaries || undefined,
        persona.name
      );
      const result = await streamingChat.sendStreaming({
        messages: apiMessages,
        clientId: clientData.clientId,
        chatType: "ayu",
        ...(activeSessionType ? { sessionType: activeSessionType } : {}),
      });

      const { cleanText, suggestion } = parseCoachingStyle(result.content);
      if (result.sessionId) setCurrentSessionId(result.sessionId);
      if (suggestion && !coachingSuggestion) {
        setCoachingSuggestion(suggestion);
      }

      setMessages(prev => {
        const withoutStreaming = prev.filter(m => m.id !== "streaming-ayu");
        return [
          ...withoutStreaming,
          {
            id: `ayu-${Date.now()}`,
            role: "ayu" as const,
            text: cleanText,
            time: formatTime(),
          },
        ];
      });
    } catch (err) {
      log.error("Chat error:");
      setError(t("coach.verbindungsproblem"));
      setMessages(prev => prev.filter(m => m.id !== "streaming-ayu"));
    } finally {
      setIsTyping(false);
      isSendingRef.current = false;
    }
  }, [
    input,
    isTyping,
    messages,
    streamingChat,
    clientData.clientId,
    clientData.ayuCoachPrompt,
    coachingSuggestion,
    memoryContext,
    pendingImage,
    uploadImageMutation,
    documentSummaries,
    activeSessionType,
  ]);

  // Image Upload Handler
  const handleImageSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setError(t("coach.errorImageType"));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(t("coach.errorImageSize"));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1];
        const preview = reader.result as string;
        setPendingImage({ base64, mimeType: file.type, preview });
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTopicClick = (topic: string) => {
    setInput(`Ich möchte heute über das Thema "${topic}" sprechen.`);
    inputRef.current?.focus();
  };

  // Voice Input Handler
  const handleVoiceToggle = useCallback(async () => {
    if (voice.isRecording) {
      const text = await voice.stopRecording();
      if (text) {
        setInput(prev => (prev ? prev + " " + text : text));
        inputRef.current?.focus();
      }
    } else {
      await voice.startRecording();
    }
  }, [voice]);

  const loadSessionQuery = trpc.chat.loadSession.useQuery(
    { sessionId: viewingSessionId! },
    { enabled: !!viewingSessionId, refetchOnWindowFocus: false }
  );
  const utils = trpcUtils; // Alias für Kompatibilität (deklariert am Anfang der Komponente)

  // Lade alte Sitzung wenn ausgewählt
  useEffect(() => {
    if (!loadSessionQuery.data || !viewingSessionId) return;
    const loaded: Message[] = dedupeMessages(
      loadSessionQuery.data.messages
        .filter(m => m.role !== "system")
        .map(m => ({
          id: `old-${m.id}`,
          role: m.role === "user" ? ("user" as const) : ("ayu" as const),
          text: m.content,
          time: formatTime(m.createdAtMs),
          imageUrl: m.imageUrl,
        }))
    );
    setViewingMessages(loaded);
  }, [loadSessionQuery.data, viewingSessionId]);

  const handleSelectOldSession = useCallback((sessionId: number) => {
    setViewingSessionId(sessionId);
    setViewingMessages(null);
  }, []);

  const handleBackToCurrentSession = useCallback(() => {
    setViewingSessionId(null);
    setViewingMessages(null);
  }, []);

  const reopenSessionMutation = trpc.chat.reopenSession.useMutation();

  const handleContinueSession = useCallback(
    async (sessionId: number) => {
      if (!clientData.clientId) return;
      try {
        // Check if this is a voice session by looking at sessions data
        const sessionsData = utils.chat.getSessions.getData({
          clientId: clientData.clientId,
          chatType: "ayu_and_voice",
        });
        const targetSession = sessionsData?.find(s => s.id === sessionId);
        const isVoiceSession = targetSession?.chatType === "voice";

        // Reopen the old session on the server (closes current active session)
        await reopenSessionMutation.mutateAsync({
          sessionId,
          clientId: clientData.clientId,
          chatType: isVoiceSession ? "voice" : "ayu",
        });

        if (isVoiceSession) {
          // For voice sessions, open the VoiceChatOverlay with continue context
          setCurrentSessionId(sessionId);
          setContinueVoiceSessionId(sessionId);
          setViewingSessionId(null);
          setViewingMessages(null);
          setShowVoiceChat(true);
          utils.chat.getHistory.invalidate();
          utils.chat.getSessions.invalidate();
        } else {
          // For text sessions, load messages into the main chat
          const loadResult = await utils.chat.loadSession.fetch({ sessionId });
          if (loadResult?.messages) {
            const loaded = loadResult.messages
              .filter(m => m.role !== "system")
              .map(m => ({
                id: `db-${m.id}`,
                role: m.role === "user" ? ("user" as const) : ("ayu" as const),
                text: m.content,
                time: formatTime(m.createdAtMs),
                imageUrl: m.imageUrl,
              }));
            setMessages(loaded);
          }
          setCurrentSessionId(sessionId);
          setSessionStarted(true);
          setHistoryLoaded(true);
          setViewingSessionId(null);
          setViewingMessages(null);
          utils.chat.getHistory.invalidate();
          utils.chat.getSessions.invalidate();
        }
      } catch (err) {
        log.error("Continue session error:");
      }
    },
    [clientData.clientId, reopenSessionMutation, utils]
  );

  const handleNewSession = useCallback(async () => {
    if (!clientData.clientId) return;

    // 1. Abort any in-progress streaming
    if (streamingChat.isStreaming) {
      streamingChat.abort();
    }

    // 2. Close current session in background (fire-and-forget)
    //    Don't await — immediate UI reset is more important
    const sid = currentSessionId;
    if (sid) {
      fetch("/api/chat/session/close", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionId: sid }),
      }).catch(err => log.warn("Close session error", { error: err?.message || String(err) }));
    }

    // 3. Immediately reset all UI state
    setMessages([]);
    setCurrentSessionId(null);
    setSessionStarted(false);
    // Keep historyLoaded=true so the history-loading effect does NOT re-run when
    // getHistory refetches. A new session has nothing to restore from the server;
    // currentSessionId will be set correctly via handleSend's response once the
    // user sends their first message. Setting this to false + invalidating getHistory
    // caused a race: the fire-and-forget session close hadn't completed before the
    // refetch fired, so the server returned the old session and the effect restored it.
    setHistoryLoaded(true);
    setCoachingSuggestion(null);
    setSuggestionDismissed(false);
    setViewingSessionId(null);
    setViewingMessages(null);
    setInput("");
    setStyleChosen(!!clientData.coachingStyle);

    // 4. Only refresh the session list in the sidebar.
    // Do NOT invalidate getHistory here — that would race with the close request
    // and restore the previous conversation via the history-loading effect.
    utils.chat.getSessions.invalidate();
  }, [
    streamingChat,
    currentSessionId,
    utils,
    clientData.clientId,
    clientData.coachingStyle,
  ]);

  const handleSessionsDeleted = useCallback((deletedIds: number[]) => {
    // If the user was viewing a session that just got deleted, go back to the current session
    if (viewingSessionId && deletedIds.includes(viewingSessionId)) {
      setViewingSessionId(null);
      setViewingMessages(null);
    }
  }, [viewingSessionId]);

  const handleAcceptCoachingStyle = useCallback(async () => {
    if (!coachingSuggestion || !clientData.clientId) return;
    try {
      await saveCoachingStyleMutation.mutateAsync({
        clientId: clientData.clientId,
        coachingStyle: coachingSuggestion.style,
      });
      setSuggestionDismissed(true);
    } catch (err) {
      log.error("Save coaching style error:");
    }
  }, [coachingSuggestion, clientData.clientId, saveCoachingStyleMutation]);

  // Lade-Zustand
  if (clientData.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  const displayMessages =
    viewingSessionId && viewingMessages ? viewingMessages : messages;
  const isViewingOld = !!viewingSessionId;
  const viewedArchivedSession = viewingSessionId
    ? (archivedSessions?.find(session => session.id === viewingSessionId) ??
      null)
    : null;
  const isViewingVoiceArchive = viewedArchivedSession?.chatType === "voice";
  const continueArchiveLabel = isViewingVoiceArchive
    ? t("coach.hierWeiterreden")
    : t("coach.hierWeiterchatten");

  return (
    <div className="flex h-full min-h-0 overflow-hidden animate-in fade-in duration-500">
      <div className="hidden h-full lg:flex">
        <SessionSidebar
          clientId={clientData.clientId || "anon"}
          chatType="ayu_and_voice"
          activeSessionId={viewingSessionId}
          currentSessionId={currentSessionId}
          onSelectSession={handleSelectOldSession}
          onNewSession={handleNewSession}
          onContinueSession={handleContinueSession}
          onSessionsDeleted={handleSessionsDeleted}
          accentColor="#6b7f4e"
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="h-full"
        />
      </div>

      {mobileArchiveOpen && (
        <>
          <button
            type="button"
            onClick={() => setMobileArchiveOpen(false)}
            className="fixed inset-x-0 top-14 bottom-20 z-30 bg-[#1a2410]/20 backdrop-blur-[1px] lg:hidden"
            aria-label="Archiv schließen"
          />
          <div className="fixed inset-y-14 left-0 bottom-20 z-40 w-[82vw] max-w-[320px] lg:hidden">
            <SessionSidebar
              clientId={clientData.clientId || "anon"}
              chatType="ayu_and_voice"
              activeSessionId={viewingSessionId}
              currentSessionId={currentSessionId}
              onSelectSession={sessionId => {
                setMobileArchiveOpen(false);
                handleSelectOldSession(sessionId);
              }}
              onNewSession={() => {
                setMobileArchiveOpen(false);
                handleNewSession();
              }}
              onContinueSession={sessionId => {
                setMobileArchiveOpen(false);
                void handleContinueSession(sessionId);
              }}
              onSessionsDeleted={handleSessionsDeleted}
              accentColor="#6b7f4e"
              onToggleCollapse={() => setMobileArchiveOpen(false)}
              className="h-full w-full max-w-none shadow-2xl"
            />
          </div>
        </>
      )}

      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">

        {/* Header */}
        <div className="sticky top-0 z-20 mb-4 flex items-center justify-between border-b border-[#d4dcc4] bg-[#f8f8f8]/95 px-4 py-4 backdrop-blur-sm lg:static lg:mb-5 lg:bg-transparent lg:px-5 lg:pt-5 lg:pb-5 lg:backdrop-blur-0">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => setMobileArchiveOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-[#d4dcc4] bg-white/80 px-3 py-1.5 text-xs font-medium text-[#5a6b4a] shadow-sm transition-colors hover:border-[#6b7f4e]/30 hover:text-[#6b7f4e] lg:hidden"
              title={t("coach.archivOeffnen")}
            >
              <Clock className="h-3.5 w-3.5" />
              {t("coach.archiv")}
            </button>
            <div className="hidden h-9 w-9 items-center justify-center rounded-xl bg-[#6b7f4e]/10 lg:flex">
              <MessageCircle className="h-[18px] w-[18px] text-[#6b7f4e]" />
            </div>
            <div className="min-w-0">
              <h1 className="font-['Playfair_Display'] text-lg font-semibold text-[#2d3a1e] sm:text-xl">
                {persona.name}
              </h1>
              <p className="truncate text-xs text-[#5a6b4a]">
                {t("coach.begleiter")}
              </p>
            </div>
          </div>
          <div className="ml-3 flex shrink-0 items-center gap-2">
            <button
              onClick={handleNewSession}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#d4dcc4] bg-white/80 px-3 py-1.5 text-xs font-medium text-[#5a6b4a] shadow-sm transition-all hover:border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/5 hover:text-[#6b7f4e] active:scale-95"
              title={t("coach.neueSession")}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t("coach.neueSession")}
            </button>
          </div>
        </div>

        {/* Sicherheits-Layer */}
        <CrisisAlert
          visible={showCrisisAlert}
          onDismiss={() => setShowCrisisAlert(false)}
          accentColor="#6b7f4e"
        />

        {/* Phänotyp-Indikator */}
        <DoshaModeIndicator
          doshaType={clientData.hasAnamnese ? "ayu-phänotyp" : undefined}
          visible={clientData.hasAnamnese}
          className="mx-4 py-2"
        />

        {/* Coaching Style Suggestion Banner */}
        {coachingSuggestion && !suggestionDismissed && (
          <div className="mx-4 mb-4 bg-gradient-to-r from-[#6b7f4e]/5 to-[#6b7f4e]/10 border border-[#d4dcc4] rounded-xl p-4 animate-in fade-in slide-in-from-top duration-500">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
                <Lightbulb className="w-4 h-4 text-[#6b7f4e]" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-[#6b7f4e] uppercase tracking-wider mb-1">
                  {t("coach.coachingStyleSuggestion")}
                </p>
                <p className="text-sm font-semibold text-[#2d3a1e]">
                  {coachingSuggestion.style}
                </p>
                <p className="text-xs text-[#5a6b4a] mt-0.5">
                  {coachingSuggestion.description}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleAcceptCoachingStyle}
                    className="text-xs bg-[#6b7f4e] text-white px-3 py-1.5 rounded-lg hover:bg-[#5a6e3f] transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    {t("coach.fitForMe")}
                  </button>
                  <button
                    onClick={() => setSuggestionDismissed(true)}
                    className="text-xs text-[#5a6b4a] px-3 py-1.5 rounded-lg hover:bg-[#f5f0e8]/5 transition-colors"
                  >
                    {t("coach.later")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div
          ref={chatScrollRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-4 pt-2 sm:px-5 lg:pb-8 lg:pr-3"
        >
          <div className={`flex min-h-full flex-col ${isViewingOld || (!sessionStarted && messages.length === 0) ? "justify-start" : "justify-end"} space-y-5`}>
            {/* Pre-Session: Style Choice + Topics */}
            {!sessionStarted && messages.length === 0 && (
              <div className="my-4 px-1 sm:my-6 sm:px-2">
                <div className="mb-6 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#6b7f4e]/10">
                    <Sparkles className="w-6 h-6 text-[#6b7f4e]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#2d3a1e]">
                    {WELCOME_TEXT}
                  </h2>
                </div>

                {/* Kommunikationsstil-Wahl */}
                {!styleChosen ? (
                  <div className="text-center">
                    <p className="mb-4 text-sm text-[#5a6b4a]">
                      {t("coach.styleQuestion")}
                    </p>
                    <div className="mx-auto flex max-w-md flex-col justify-center gap-2 sm:flex-row">
                      {COMMUNICATION_STYLES.map(style => (
                        <button
                          key={style.label}
                          onClick={async () => {
                            setStyleChosen(true);
                            const styleMsg = `Bitte sprich ${style.label.toLowerCase()} mit mir.`;
                            if (!clientData.clientId) return;
                            setSessionStarted(true);
                            const userMsg: Message = {
                              id: `user-${Date.now()}`,
                              role: "user",
                              text: styleMsg,
                              time: formatTime(),
                            };
                            setMessages([userMsg]);
                            setIsTyping(true);
                            const streamingMsg: Message = {
                              id: "streaming-ayu",
                              role: "ayu",
                              text: "",
                              time: formatTime(),
                            };
                            setMessages(prev => [...prev, streamingMsg]);
                            streamingAyuMsgRef.current = "";
                            try {
                              const apiMessages = buildApiMessages(
                                clientData.ayuCoachPrompt,
                                [userMsg],
                                memoryContext as MemoryContextData | undefined,
                                documentSummaries || undefined,
                                persona.name
                              );
                              const result = await streamingChat.sendStreaming({
                                messages: apiMessages,
                                clientId: clientData.clientId,
                                chatType: "ayu",
                              });
                              const { cleanText, suggestion } =
                                parseCoachingStyle(result.content);
                              if (result.sessionId)
                                setCurrentSessionId(result.sessionId);
                              if (suggestion && !coachingSuggestion)
                                setCoachingSuggestion(suggestion);
                              setMessages(prev => {
                                const withoutStreaming = prev.filter(
                                  m => m.id !== "streaming-ayu"
                                );
                                return [
                                  ...withoutStreaming,
                                  {
                                    id: `ayu-${Date.now()}`,
                                    role: "ayu" as const,
                                    text: cleanText,
                                    time: formatTime(),
                                  },
                                ];
                              });
                            } catch (err) {
                              log.error("Style choice error:");
                              setError(t("coach.verbindungsproblem"));
                              setMessages(prev =>
                                prev.filter(m => m.id !== "streaming-ayu")
                              );
                            } finally {
                              setIsTyping(false);
                            }
                          }}
                          className="flex flex-col items-center gap-1 rounded-xl border border-[#d4dcc4] px-4 py-3 transition-all hover:border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/5 hover:shadow-sm"
                        >
                          <span className="text-lg">{style.emoji}</span>
                          <span className="text-sm font-medium text-[#2d3a1e]">
                            {style.label}
                          </span>
                          <span className="text-[10px] text-[#5a6b4a]/60">
                            {style.description}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center gap-2">
                    {SESSION_TOPICS.map(topic => {
                      const Icon = topic.icon;
                      return (
                        <button
                          key={topic.label}
                          onClick={() => handleTopicClick(topic.label)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#d4dcc4] hover:border-[#d4dcc4] hover:shadow-sm transition-all text-sm ${topic.color}`}
                        >
                          <Icon className="w-4 h-4" />
                          {topic.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {!historyLoaded && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-2 text-xs text-[#5a6b4a]">
                  <div className="w-4 h-4 border-2 border-[#6b7f4e]/30 border-t-[#6b7f4e] rounded-full animate-spin" />
                  {t("coach.verlaufWirdGeladen")}
                </div>
              </div>
            )}

            {viewingSessionId && !viewingMessages && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
              </div>
            )}

            {(!viewingSessionId || viewingMessages) && displayMessages.map((msg, index) => (
              <div
                key={msg.id}
                ref={
                  index === displayMessages.length - 1
                    ? lastChatBubbleRef
                    : undefined
                }
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[88%] md:max-w-[78%] rounded-3xl sm:rounded-4xl px-3 py-2.5 sm:px-5 sm:py-4 ${
                    msg.role === "user"
                      ? "bg-[#6b7f4e]/10 text-[#2d3a1e] rounded-br-md"
                      : "bg-[#f0f3eb] border border-[#d4dcc4] text-[#2d3a1e] rounded-bl-md shadow-sm"
                  }`}
                >
                  {msg.role === "ayu" && (
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-[#6b7f4e]" />
<span className="text-[10px] font-medium text-[#6b7f4e] uppercase tracking-wider">
                            {persona.name}
                          </span>
                      </div>
                      {tts.isSupported && (
                        <button
                          onClick={() => tts.speak(msg.text, msg.id)}
                          className={`p-1 rounded-lg transition-all duration-200 ${
                            (tts.isSpeaking || tts.isLoading) &&
                            tts.currentMsgId === msg.id
                              ? "bg-[#6b7f4e]/15 text-[#6b7f4e]"
                              : "text-[#5a6b4a]/30 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5"
                          }`}
                          title={
                            tts.isLoading && tts.currentMsgId === msg.id
                              ? t("coach.loading")
                              : tts.isSpeaking && tts.currentMsgId === msg.id
                                ? t("coach.stop")
                                : t("coach.readAloud")
                          }
                          disabled={
                            tts.isLoading && tts.currentMsgId !== msg.id
                          }
                        >
                          {tts.isLoading && tts.currentMsgId === msg.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : tts.isSpeaking && tts.currentMsgId === msg.id ? (
                            <VolumeX className="w-3.5 h-3.5" />
                          ) : (
                            <Volume2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  {msg.imageUrl && (
                    <div className="mb-3">
                      <img
                        src={msg.imageUrl}
                        alt={t("coach.imageUploaded")}
                        className="max-w-full max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(msg.imageUrl, "_blank")}
                      />
                    </div>
                  )}
                  <ChatMarkdown
                    text={msg.text}
                    isStreaming={msg.id === "streaming-ayu"}
                    className="text-xs sm:text-sm leading-relaxed"
                  />
                  {/* Rezept speichern Button — nur bei fertigen AYU-Nachrichten */}
                  {msg.role === "ayu" && msg.id !== "streaming-ayu" && (
                    <SaveRecipeButton
                      messageText={msg.text}
                      clientId={clientData.clientId || "anon"}
                      messageId={msg.id}
                    />
                  )}
                  <p
                    className={`mt-2 text-[10px] ${msg.role === "user" ? "text-[#5a6b4a]/30 text-right" : "text-[#5a6b4a]/25"}`}
                  >
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}

            {/* Quick-Reply Vorschläge */}
            {!isTyping &&
              !isViewingOld &&
              messages.length > 0 &&
              messages[messages.length - 1]?.role === "ayu" &&
              messages[messages.length - 1]?.id !== "streaming-ayu" && (
                <QuickReplies
                  lastAssistantMessage={messages[messages.length - 1]?.text}
                  chatType="ayu"
                  onSelect={text => {
                    setInput(text);
                    setTimeout(() => {
                      handleSend();
                    }, 100);
                  }}
                />
              )}

            {/* Typing Indicator */}
            {isTyping &&
              !messages.some(
                m => m.id === "streaming-ayu" && m.text.length > 0
              ) && (
                <div className="flex justify-start">
                  <div className="rounded-[24px] rounded-bl-md border border-[#d4dcc4] bg-[#f0f3eb] px-5 py-4 shadow-sm">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Sparkles className="w-3 h-3 text-[#6b7f4e]" />
                      <span className="text-[10px] font-medium text-[#6b7f4e] uppercase tracking-wider">
                        {persona.name}
                      </span>
                    </div>
                    <div className="flex gap-1.5 py-1">
                      <div
                        className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <div
                        className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      />
                      <div
                        className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Error Message */}
            {(error || voice.error) && (
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-xs text-red-500/70 bg-red-900/20 px-3 py-2 rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  {error || voice.error}
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Mobile FAB: floats above the input bar */}
          <div className="sm:hidden fixed right-4 bottom-40 z-20">
            <button
              onClick={() => setShowVoiceChat(true)}
              className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-[#6b7f4e] to-[#5a6e3f] text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 active:scale-95"
              title={t("coach.speakWith").replace("{name}", persona.name)}
            >
              <div className="relative">
                <Phone className="w-6 h-6" />
                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 animate-pulse ring-2 ring-white/40" />
              </div>
            </button>
          </div>
        </div>

        {isViewingOld && viewingSessionId && (
          <div className="shrink-0 bg-[#faf8f3] px-5 py-2">
            <div className="rounded-2xl border border-[#d4dcc4] bg-white/80 px-4 py-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#6b7f4e]">{t("coach.archiv")}</span>
                    <span className="text-[10px] text-[#5a6b4a]/50">·</span>
                    <span className="text-[11px] text-[#5a6b4a]/70">
                      {t("coach.vergangeneSession").replace("{type}", isViewingVoiceArchive ? t("coach.sprachSitzung") : t("coach.chatSitzung"))}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#5a6b4a]/70">
                    {isViewingVoiceArchive
                      ? t("coach.anrufFortsetzen")
                      : t("coach.chatFortsetzen")}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleContinueSession(viewingSessionId)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#6b7f4e] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#5a6e3f]"
                  >
                    {isViewingVoiceArchive ? (
                      <Phone className="h-4 w-4" />
                    ) : (
                      <MessageCircle className="h-4 w-4" />
                    )}
                    {continueArchiveLabel}
                  </button>
                  <button
                    onClick={handleBackToCurrentSession}
                    className="inline-flex items-center gap-2 rounded-full border border-[#d4dcc4] px-4 py-2 text-sm font-medium text-[#5a6b4a] transition-colors hover:border-[#6b7f4e]/30 hover:text-[#6b7f4e]"
                  >
                    {t("coach.zurueckAktuellerChat")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Input Area (nur bei aktueller Sitzung) */}
        {!isViewingOld && (
          <div className="shrink-0 bg-[#f8f8f8]/95 mt-1 px-1 backdrop-blur-sm lg:border-t-0 lg:bg-[#f8f8f8] lg:px-1 lg:pb-1 lg:backdrop-blur-0">
            {/* Recording Indicator */}
            {voice.isRecording && (
              <div className="flex items-center justify-between bg-[#6b7f4e]/5 border border-[#d4dcc4] rounded-xl px-4 py-2.5 mb-2 mx-4 animate-in fade-in duration-300">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                    <div className="absolute inset-0 w-3 h-3 rounded-full bg-red-500/30 animate-ping" />
                  </div>
                  <span className="text-xs text-[#5a6b4a] font-medium">
                    {t("coach.recordingRunning").replace("{duration}", String(voice.duration))}
                  </span>
                </div>
                <button
                  onClick={voice.cancelRecording}
                  className="text-[#5a6b4a]/40 hover:text-red-500 transition-colors"
                  title={t("coach.recordingCancel")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Transcribing Indicator */}
            {voice.isTranscribing && (
              <div className="flex items-center gap-2 bg-[#6b7f4e]/5 border border-[#d4dcc4] rounded-xl px-4 py-2.5 mb-2 mx-4 animate-in fade-in duration-300">
                <Loader2 className="w-3.5 h-3.5 text-[#6b7f4e] animate-spin" />
                <span className="text-xs text-[#5a6b4a] font-medium">
                  {t("coach.recognizing")}
                </span>
              </div>
            )}

            {/* Image Preview */}
            {pendingImage && (
              <div className="flex items-center gap-3 bg-[#6b7f4e]/5 border border-[#d4dcc4] rounded-xl px-3 py-2 mb-2 mx-4 animate-in fade-in duration-300">
                <img
                  src={pendingImage.preview}
                  alt="Vorschau"
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <span className="text-xs text-[#5a6b4a] flex-1">
                  {t("coach.imageReady")}
                </span>
                {imageUploading && (
                  <Loader2 className="w-4 h-4 text-[#6b7f4e] animate-spin" />
                )}
                <button
                  onClick={() => setPendingImage(null)}
                  className="text-[#5a6b4a]/40 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ─── Stimme + Sprechen-Button (nur Desktop) ─── */}
            <div className="hidden sm:flex mx-auto mt-3 w-full max-w-3xl flex-col items-center gap-4 border-t border-[#d4dcc4] px-5 pt-4">
              {/* TTS Lautsprecher + Stimme-Auswahl */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (tts.isSpeaking) {
                      tts.stop();
                    } else {
                      const lastAyu = [...messages]
                        .reverse()
                        .find(m => m.role === "ayu");
                      if (lastAyu) tts.speak(lastAyu.text, lastAyu.id);
                    }
                  }}
                  className={`p-1.5 rounded-full transition-all ${
                    tts.isSpeaking
                      ? "bg-[#6b7f4e]/20 text-[#6b7f4e]"
                      : "text-[#5a6b4a]/40 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/10"
                  }`}
                  title={
                    tts.isSpeaking
                      ? t("coach.speaking")
                      : t("coach.lastAnswer")
                  }
                >
                  {tts.isSpeaking ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => {
                    const newPref =
                      clientData.voicePreference === "annam"
                        ? "christopher"
                        : "annam";
                    saveVoicePreferenceMutation.mutate({
                      clientId: clientData.clientId || "anon",
                      voicePreference: newPref,
                    });
                  }}
                  className="text-xs text-[#5a6b4a]/60 hover:text-[#5a6b4a]/90 bg-[#6b7f4e]/5 hover:bg-[#6b7f4e]/10 px-3 py-1.5 rounded-full transition-all"
                >
                  {t("coach.voice")}{" "}
                  <span className="font-semibold">
                    {clientData.voicePreference === "christopher"
                      ? "Christopher ♂"
                      : "Annam ♀"}
                  </span>
                </button>
              </div>

              {/* Mit Coach sprechen Button */}
              <button
                onClick={() => setShowVoiceChat(true)}
                className="flex items-center gap-3 bg-gradient-to-r from-[#6b7f4e] to-[#5a6e3f] hover:from-[#5a6e3f] hover:to-[#4d6035] text-white px-6 py-3 rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <div className="relative">
                  <Phone className="w-5 h-5" />
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse ring-2 ring-white/30" />
                </div>
                <span className="text-sm font-semibold tracking-wide">
                  {t("coach.speakWith").replace("{name}", persona.name)}
                </span>
              </button>
            </div>

            {/* ─── Text-Eingabe ganz unten ─── */}
            <div className="mx-auto w-full max-w-3xl px-2 sm:px-4 py-1 sm:pb-2">
              <div className="flex items-end gap-2 bg-[#f0f3eb] rounded-2xl border border-[#d4dcc4] p-2 focus-within:border-[#6b7f4e]/30 focus-within:shadow-sm transition-all">
                {/* Mikrofon-Button */}
                <button
                  onClick={handleVoiceToggle}
                  disabled={voice.isTranscribing || isTyping}
                  className={`p-2 rounded-xl transition-all duration-200 shrink-0 ${
                    voice.isRecording
                      ? "bg-red-500 text-white hover:bg-red-600 shadow-sm animate-pulse"
                      : voice.isTranscribing
                        ? "bg-[#f5f0e8]/5 text-[#5a6b4a]/30 cursor-not-allowed"
                        : "bg-[#6b7f4e]/10 text-[#6b7f4e] hover:bg-[#6b7f4e]/20"
                  }`}
                  title={
                    voice.isRecording
                      ? t("coach.recordingStop")
                      : t("coach.startVoice")
                  }
                >
                  {voice.isRecording ? (
                    <MicOff className="w-4 h-4" />
                  ) : voice.isTranscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </button>

                {/* Bild-Upload-Button */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isTyping || voice.isRecording || imageUploading}
                  className={`p-2 rounded-xl transition-all duration-200 shrink-0 ${
                    pendingImage
                      ? "bg-[#6b7f4e]/20 text-[#6b7f4e]"
                      : "bg-[#6b7f4e]/10 text-[#6b7f4e] hover:bg-[#6b7f4e]/20"
                  } ${isTyping || voice.isRecording || imageUploading ? "opacity-30 cursor-not-allowed" : ""}`}
                  title={t("coach.uploadImage")}
                >
                  <ImagePlus className="w-4 h-4" />
                </button>

                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => {
                    setInput(e.target.value);
                    const el = e.target;
                    el.style.height = "auto";
                    el.style.height = Math.min(el.scrollHeight, 128) + "px";
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    voice.isRecording
                      ? t("coach.speakNow")
                      : pendingImage
                        ? t("coach.imageQuestion")
                        : t("coach.nachricht")
                  }
                  rows={1}
                  className="flex-1 resize-none bg-transparent text-sm text-[#2d3a1e] placeholder:text-[#5a6b4a]/30 focus:outline-none px-2 py-1.5 max-h-32"
                  style={{ minHeight: "36px" }}
                  disabled={voice.isRecording}
                />
                <button
                  onClick={handleSend}
                  disabled={
                    (!input.trim() && !pendingImage) ||
                    isTyping ||
                    imageUploading ||
                    voice.isRecording
                  }
                  className={`p-2 rounded-xl transition-all duration-200 ${
                    (input.trim() || pendingImage) &&
                    !isTyping &&
                    !imageUploading &&
                    !voice.isRecording
                      ? "bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] shadow-sm"
                      : "bg-[#f5f0e8]/5 text-[#5a6b4a]/30 cursor-not-allowed"
                  }`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-[#5a6b4a]/30 text-center mt-2">
                {t("coach.historyNote")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Voice Chat Overlay */}
      {showVoiceChat && (
        <VoiceChatOverlay
          clientId={clientData.clientId || "anon"}
          voicePreference={clientData.voicePreference as "annam" | "christopher"}
          onClose={() => {
            setShowVoiceChat(false);
            setActiveSessionType(undefined);
            setActiveSessionTitle(undefined);
            setContinueVoiceSessionId(null);
            utils.chat.getSessions.invalidate();
          }}
          sessionType={activeSessionType}
          sessionTitle={activeSessionTitle}
          continueSessionId={continueVoiceSessionId}
          onVoicePreferenceChange={pref => {
            saveVoicePreferenceMutation.mutate({
              clientId: clientData.clientId || "anon",
              voicePreference: pref,
            });
          }}
          onTranscriptCommit={handleVoiceTranscriptCommit}
        />
      )}
    </div>
  );
}
